import {MutableRefObject, ReactNode, useRef} from "react";
import {Controller, get, useFieldArray, useFormContext} from "react-hook-form";
import {usePricingCurrencies} from "@/features/currencies";
import {Button, Col, Input, Row, Select, Space, Tooltip} from "antd";
import {DeleteOutlined, PlusCircleFilled} from "@ant-design/icons";
import {UiTooltip} from "../../../../../design-system/tooltip";
import {FormFieldError} from "../../../../../design-system/form-field";
import {StyledInputLabelWrapper} from "../../../../input/input.style";
import {AutomaticCurrencyConversionNotice} from "../../../../shared/automatic-currency-conversion";
import {useCurrencyOptions} from "../hooks/currency";
import {PwywCurrenciesFormFieldsType} from "./types";

export const MINIMUM_PRICE_LABEL = "Minimum price";
export const SUGGESTED_PRICE_LABEL = "Suggested price";
export const MINIMUM_PRICE_TOOLTIP = "Your customers cannot pay less than this amount.";
export const SUGGESTED_PRICE_TOOLTIP =
  "Your customers will see this amount as the suggested payment. They have the flexibility to pay less or more than this recommended amount.";

/**
 * An amount the coach has not entered yet — an empty input.
 *
 * `Currency` types `callPrice` as a number because a Single Fee always has one. A Pay
 * What You Want row genuinely has neither amount until the coach types them, and the
 * schema is what makes them do so — so a row starts empty rather than at a number the
 * validation would reject anyway.
 *
 * It is `""` and not `undefined` because react-hook-form falls back to `defaultValues`
 * for a field it holds no value for: an amount cleared to `undefined` reappears as
 * whatever the offering was last saved with. Both the row mapper and the schema already
 * read `""` as "not entered".
 */
export const UNSET_PWYW_AMOUNT = "" as unknown as number;

/**
 * The Minimum Price and Suggested Price rows a coach fills in under Pay What You Want.
 *
 * Shared by every offering type, and rendered with no props: the currency-options
 * bookkeeping is the same for all of them, and the rows are read from form context. An
 * offering type adds Pay What You Want by rendering this and nothing else.
 *
 * Only the Minimum Price carries the currency selector; the Suggested Price inherits its
 * row's currency, because a row is one indivisible unit of currency and two amounts.
 *
 * Validation waits for a blur. Correcting a Minimum Price re-checks its row's Suggested
 * Price straight away, but only once the coach has left that field — otherwise the form
 * would be marking a number they have not finished typing.
 */
export function PwywCurrenciesFormField() {
  const pricingCurrencies = usePricingCurrencies();
  const {control, watch} = useFormContext<PwywCurrenciesFormFieldsType>();

  const {
    fields,
    append: appendCurrency,
    remove: removeCurrency,
  } = useFieldArray({control, name: "currencies"});

  /** Rows whose Suggested Price the coach has already left once. */
  const suggestedPriceLeftOnce = useRef(new Set<string>());

  const rows = watch("currencies");

  /** Row one's currency — the one automatic conversion converts from. */
  const baseCurrency =
    rows.length > 0 ? rows[0].callPriceCurrency : pricingCurrencies.formCurrencies[0];

  const {
    currencyOptions,
    handleCurrencySelectChange: onCurrencySelect,
    removeCurrency: onRemoveCurrency,
    addNewCurrency: onAddCurrency,
  } = useCurrencyOptions(rows, baseCurrency, appendCurrency, removeCurrency, {
    callPrice: UNSET_PWYW_AMOUNT,
    minCallPrice: UNSET_PWYW_AMOUNT,
  });

  return (
    <Space direction="vertical" size={24} style={{display: "flex"}}>
      <Row gutter={[16, 24]}>
        {fields.map((item, index) => {
          const rowCurrency = rows?.[index]?.callPriceCurrency ?? "";

          return (
            <PwywCurrencyRowFields
              key={item.id}
              name="currencies"
              index={index}
              rowKey={item.id}
              suggestedPriceLeftOnce={suggestedPriceLeftOnce}
              currencyAddon={(currencyError) => (
                <Controller
                  control={control}
                  name={`currencies.${index}.callPriceCurrency`}
                  render={({field: currencyField}) => (
                    <Select
                      aria-label={`${MINIMUM_PRICE_LABEL} currency`}
                      status={currencyError ? "error" : ""}
                      ref={currencyField.ref}
                      value={currencyField.value}
                      size="large"
                      showSearch={false}
                      notFoundContent={null}
                      onChange={(selected) =>
                        onCurrencySelect(selected, currencyField.onChange)
                      }
                    >
                      {currencyOptions.map((option) => (
                        <Select.Option key={option} value={option}>
                          {option}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                />
              )}
              removeControl={
                <Tooltip
                  title={fields.length === 1 ? "At least one currency is required" : null}
                >
                  <Button
                    onClick={() => onRemoveCurrency(index)}
                    type="link"
                    shape="circle"
                    icon={<DeleteOutlined />}
                    size="small"
                    disabled={fields.length === 1}
                    aria-label={`Remove ${rowCurrency || "currency"}`}
                  />
                </Tooltip>
              }
            />
          );
        })}
      </Row>
      <div>
        <Button icon={<PlusCircleFilled />} onClick={onAddCurrency} size="small">
          Add a currency
        </Button>
        <div style={{marginTop: "2.4rem"}}>
          <AutomaticCurrencyConversionNotice sourceCurrency={baseCurrency} />
        </div>
      </div>
    </Space>
  );
}

/**
 * Pay What You Want rows whose currencies are decided elsewhere — a subscription tier's,
 * which always match the subscription's Currency Config.
 *
 * Each row shows its currency as a label, with no picker, no remove and no add: the
 * Currency Config owns those. The rows are read rather than registered as a field array,
 * because the Currency Config already holds one on the same rows and two would drift.
 *
 * @param name - where the rows live in the form, e.g. `tiers.1.currencies`
 */
export function FixedPwywCurrenciesFormField({name}: {name: string}) {
  const {watch} = useFormContext();
  const suggestedPriceLeftOnce = useRef(new Set<string>());
  const rows: {callPriceCurrency?: string}[] = watch(name) ?? [];

  return (
    <Row gutter={[16, 24]}>
      {rows.map((row, index) => {
        const rowCurrency = row?.callPriceCurrency ?? "";
        // Keyed by currency: the Currency Config keeps them unique, and a row keeps its
        // state when an earlier currency is removed.
        const rowKey = rowCurrency || `row-${index}`;

        return (
          <PwywCurrencyRowFields
            key={rowKey}
            name={name}
            index={index}
            rowKey={rowKey}
            suggestedPriceLeftOnce={suggestedPriceLeftOnce}
            currencyAddon={() => (
              <span style={{minWidth: 48, display: "inline-block"}}>{rowCurrency}</span>
            )}
          />
        );
      })}
    </Row>
  );
}

/**
 * One currency row: its Minimum Price, carrying the currency, and its Suggested Price.
 *
 * @param name - where the rows live in the form
 * @param currencyAddon - what sits before the Minimum Price, given the row's currency error
 * @param removeControl - an optional control beside the row for removing it
 */
function PwywCurrencyRowFields({
  name,
  index,
  rowKey,
  suggestedPriceLeftOnce,
  currencyAddon,
  removeControl,
}: {
  name: string;
  index: number;
  rowKey: string;
  suggestedPriceLeftOnce: MutableRefObject<Set<string>>;
  currencyAddon: (currencyError: string | undefined) => ReactNode;
  removeControl?: ReactNode;
}) {
  const {
    control,
    trigger,
    watch,
    formState: {errors},
  } = useFormContext();
  const rowPath = `${name}.${index}`;
  const rowCurrency: string = watch(`${rowPath}.callPriceCurrency`) ?? "";
  const rowErrors = get(errors, rowPath);
  const currencyError: string | undefined = rowErrors?.callPriceCurrency?.message;
  // A row with no currency is a row whose Minimum Price has nothing to be in, so it reads
  // against that field rather than opening a third error slot.
  const minimumPriceError: string | undefined =
    currencyError ?? rowErrors?.minCallPrice?.message;
  const suggestedPriceError: string | undefined = rowErrors?.callPrice?.message;

  function revalidateSuggestedPrice() {
    if (suggestedPriceLeftOnce.current.has(rowKey)) {
      trigger(`${rowPath}.callPrice`);
    }
  }

  return (
    <>
      <Col span={24} sm={12}>
        <div style={{display: "flex", gap: "1.6rem"}}>
          <div style={{flex: "1"}}>
            <StyledInputLabelWrapper>
              {/* The info tip sits beside the label, not inside it: a button in a
                  label steals the click meant for the field. */}
              <label htmlFor={`${rowPath}.minCallPrice`}>{MINIMUM_PRICE_LABEL}</label>{" "}
              <UiTooltip title={MINIMUM_PRICE_TOOLTIP} />
            </StyledInputLabelWrapper>
            <Controller
              control={control}
              name={`${rowPath}.minCallPrice`}
              render={({field: {onChange, onBlur, value, ref}}) => (
                <Input
                  id={`${rowPath}.minCallPrice`}
                  // Named outright: antd wraps the input in its addon group, so the
                  // visible label alone does not reliably name it.
                  aria-label={MINIMUM_PRICE_LABEL}
                  ref={ref}
                  size="large"
                  type="text"
                  min={0}
                  status={minimumPriceError ? "error" : ""}
                  value={value ?? ""}
                  placeholder=""
                  onChange={(event) => {
                    onChange(event);
                    revalidateSuggestedPrice();
                  }}
                  onBlur={() => {
                    onBlur();
                    trigger(`${rowPath}.minCallPrice`);
                    revalidateSuggestedPrice();
                  }}
                  addonBefore={currencyAddon(currencyError)}
                />
              )}
            />
            <FormFieldError errorMessage={minimumPriceError} />
          </div>
          {removeControl ? <div style={{marginTop: "auto"}}>{removeControl}</div> : null}
        </div>
      </Col>
      <Col span={24} sm={12}>
        <StyledInputLabelWrapper>
          <label htmlFor={`${rowPath}.callPrice`}>{SUGGESTED_PRICE_LABEL}</label>{" "}
          <UiTooltip title={SUGGESTED_PRICE_TOOLTIP} />
        </StyledInputLabelWrapper>
        <Controller
          control={control}
          name={`${rowPath}.callPrice`}
          render={({field: {onChange, onBlur, value, ref}}) => (
            <Input
              id={`${rowPath}.callPrice`}
              aria-label={SUGGESTED_PRICE_LABEL}
              ref={ref}
              size="large"
              type="text"
              min={0}
              status={suggestedPriceError ? "error" : ""}
              value={value ?? ""}
              // The row's currency code and nothing else. Showing the Minimum Price
              // here would demonstrate an amount the validation rejects.
              placeholder={rowCurrency}
              onChange={onChange}
              onBlur={() => {
                onBlur();
                suggestedPriceLeftOnce.current.add(rowKey);
                trigger(`${rowPath}.callPrice`);
              }}
            />
          )}
        />
        <FormFieldError errorMessage={suggestedPriceError} />
      </Col>
    </>
  );
}
